import { navigate } from '../boot/router';
import type { AppUser } from '../boot/session';
import {
  listFavoriteDrawings,
  listMyReviewQueue,
  listProjects,
  listRecentDrawings,
} from '../data/repo';
import { useAsync } from '../hooks/useAsync';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { EmptyState, ErrorState, LoadingState } from '../shell/State';
import { relTime, VersionStatusBadge } from '../shell/bits';
import type { DrawingRow } from '../data/types';

/** 대시보드 — 최근 도면 · 즐겨찾기 · 검토 요청 (작업지시서 P1-06) */
export function DashboardPage({ user }: { user: AppUser }) {
  const projects = useAsync(() => listProjects(false), []);
  const recent = useAsync(() => listRecentDrawings(6), []);
  const favorites = useAsync(() => listFavoriteDrawings(), []);
  const reviews = useAsync(() => listMyReviewQueue(), []);
  const { nameOf } = useUserDirectory();

  const loading = projects.loading || recent.loading || reviews.loading;
  const error = projects.error ?? recent.error ?? reviews.error ?? favorites.error;

  if (loading) return <LoadingState label="대시보드를 준비하는 중…" />;

  if (error) {
    return (
      <div className="page">
        <ErrorState
          message={error}
          onRetry={() => {
            projects.reload();
            recent.reload();
            favorites.reload();
            reviews.reload();
          }}
        />
      </div>
    );
  }

  const projectList = projects.data ?? [];
  const drawingTotal = projectList.reduce((s, p) => s + p.drawing_count, 0);
  const reviewList = reviews.data ?? [];

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">대시보드</h1>
          <p className="page__desc">
            {user.name || user.email} 님, 안녕하세요.
          </p>
        </div>
      </header>

      <div className="stats">
        <div className="stat">
          <div className="stat__label">진행 중 프로젝트</div>
          <div className="stat__value">{projectList.length}</div>
        </div>
        <div className="stat">
          <div className="stat__label">전체 도면</div>
          <div className="stat__value">{drawingTotal}</div>
        </div>
        <div className="stat">
          <div className="stat__label">내 검토 대기</div>
          <div className={reviewList.length > 0 ? 'stat__value stat__value--accent' : 'stat__value'}>
            {reviewList.length}
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">즐겨찾기</div>
          <div className="stat__value">{(favorites.data ?? []).length}</div>
        </div>
      </div>

      {/* 검토 대기가 있으면 가장 위에 — 사용자가 지금 해야 할 일 */}
      {reviewList.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">내 검토 대기</h2>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>도면</th>
                  <th>프로젝트</th>
                  <th>버전</th>
                  <th>요청자</th>
                  <th>요청</th>
                </tr>
              </thead>
              <tbody>
                {reviewList.map((r) => (
                  <tr
                    key={r.version_id}
                    className="row-link"
                    onClick={() =>
                      navigate({
                        name: 'version',
                        drawingId: r.drawing_id,
                        versionId: r.version_id,
                      })
                    }
                  >
                    <td className="row-link__name">{r.drawing_name}</td>
                    <td>{r.project_name}</td>
                    <td>v{r.version_number}</td>
                    <td title={r.created_by}>{nameOf(r.created_by)}</td>
                    <td>{relTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <DrawingSection
        title="최근 작업한 도면"
        rows={recent.data ?? []}
        emptyTitle="아직 작업한 도면이 없습니다"
        emptyDesc="프로젝트를 열어 첫 도면을 만들어 보세요."
        showGoProjects
      />

      {(favorites.data ?? []).length > 0 && (
        <DrawingSection
          title="즐겨찾기"
          rows={favorites.data ?? []}
          emptyTitle=""
          emptyDesc=""
        />
      )}
    </div>
  );
}

function DrawingSection({
  title,
  rows,
  emptyTitle,
  emptyDesc,
  showGoProjects,
}: {
  title: string;
  rows: DrawingRow[];
  emptyTitle: string;
  emptyDesc: string;
  showGoProjects?: boolean;
}) {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">{title}</h2>
        <button
          type="button"
          className="section__more"
          onClick={() => navigate({ name: 'projects' })}
        >
          프로젝트 전체 보기 →
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="⬚"
            title={emptyTitle}
            desc={emptyDesc}
            action={
              showGoProjects ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  style={{ marginTop: 10 }}
                  onClick={() => navigate({ name: 'projects' })}
                >
                  프로젝트로 이동
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>도면명</th>
                <th>층</th>
                <th>최신 버전</th>
                <th>상태</th>
                <th>수정</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className="row-link"
                  onClick={() => navigate({ name: 'drawing', drawingId: d.id })}
                >
                  <td className="row-link__name">
                    {d.is_favorite && <span aria-label="즐겨찾기">★ </span>}
                    {d.name}
                  </td>
                  <td>{d.floor_name ?? '—'}</td>
                  <td>{d.latest_version ? `v${d.latest_version.version_number}` : '—'}</td>
                  <td>
                    {d.latest_version ? <VersionStatusBadge status={d.latest_version.status} /> : '—'}
                  </td>
                  <td>{relTime(d.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
